export type FormatPrefs = {
  maxHeight?: number;
  maxFps?: number;
  preferAvc?: boolean;
  container?: 'mp4' | 'webm' | 'mkv';
};

export function buildFormatSelector(p: FormatPrefs = {}): string {
  // Start with the most robust fallback chain per yt-dlp docs
  const selectors: string[] = [];
  
  // Build constraint filters
  const constraints: string[] = [];
  if (typeof p.maxHeight === 'number' && Number.isFinite(p.maxHeight) && p.maxHeight > 0) {
    constraints.push(`height<=${Math.floor(p.maxHeight)}`);
  }
  if (typeof p.maxFps === 'number' && Number.isFinite(p.maxFps) && p.maxFps > 0) {
    constraints.push(`fps<=${Math.floor(p.maxFps)}`);
  }
  
  const constraintSuffix = constraints.length > 0 
    ? constraints.map(rule => `[${rule}]`).join('') 
    : '';

  // Add codec preferences if specified
  if (p.preferAvc) {
    selectors.push(`bestvideo${constraintSuffix}[vcodec*=avc1]+bestaudio/best`);
  }

  // Add container-specific preferences
  if (p.container === 'mp4') {
    selectors.push(`bestvideo${constraintSuffix}[ext=mp4]+bestaudio[ext=m4a]/best[ext=mp4]`);
  } else if (p.container === 'webm') {
    selectors.push(`bestvideo${constraintSuffix}[ext=webm]+bestaudio[ext=webm]/best[ext=webm]`);
  }

  // Always add generic fallbacks
  selectors.push(`bestvideo${constraintSuffix}+bestaudio/best`);
  selectors.push('best');

  return selectors.join('/');
}

/**
 * Format bytes to human-readable string
 */
export function formatBytes(value?: number | string | null): string {
  if (value === undefined || value === null) return 'unknown size';
  const numeric = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(numeric) || numeric <= 0) return 'unknown size';
  
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let idx = 0;
  let num = numeric;
  
  while (num >= 1024 && idx < units.length - 1) {
    num /= 1024;
    idx += 1;
  }
  
  return `${num.toFixed(num >= 10 || idx === 0 ? 0 : 1)} ${units[idx]}`;
}

/**
 * Format speed (bytes per second) to human-readable string
 */
export function formatSpeed(value?: number): string {
  if (!value || value <= 0) return '—';
  return `${formatBytes(value)}/s`;
}

/**
 * Format ETA (estimated time remaining) in seconds to human-readable string
 */
export function formatEta(value?: number): string {
  if (!value || value <= 0) return '≈ ready';
  const minutes = Math.floor(value / 60);
  const seconds = Math.floor(value % 60);
  if (minutes > 0) {
    return `${minutes}m ${seconds}s remaining`;
  }
  return `${seconds}s remaining`;
}

/**
 * Format view count to human-readable string
 */
export function formatViewCount(value?: number): string {
  if (value === undefined) return '—';
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return value.toLocaleString();
}

/**
 * Format duration in seconds to human-readable string
 */
export function formatDuration(seconds?: number): string {
  if (!seconds || seconds <= 0) return '—';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Format percentage with proper rounding
 */
export function formatPercentage(value?: number, total?: number): string {
  if (value === undefined || total === undefined || total <= 0) return '0%';
  const percent = Math.min(100, Math.round((value / total) * 100));
  return `${percent}%`;
}

/**
 * Format file size with appropriate precision
 */
export function formatFileSize(bytes: number): string {
  return formatBytes(bytes);
}

/**
 * Format download progress with all relevant information
 */
export function formatDownloadProgress(progress: {
  downloadedBytes: number;
  totalBytes?: number;
  speedBps?: number;
  etaSec?: number;
}): string {
  const percent = progress.totalBytes 
    ? formatPercentage(progress.downloadedBytes, progress.totalBytes)
    : 'Unknown';
  const downloaded = formatBytes(progress.downloadedBytes);
  const total = progress.totalBytes ? formatBytes(progress.totalBytes) : 'unknown';
  const speed = progress.speedBps ? ` at ${formatSpeed(progress.speedBps)}` : '';
  const eta = progress.etaSec ? ` · ${formatEta(progress.etaSec)}` : '';
  
  return `${percent} (${downloaded}/${total})${speed}${eta}`;
}
